import React, { type JSX, createContext, useContext, useState } from 'react';

import { FlashMessage, TFlashMessage } from '@/core/ui/FlashMessage';
import { useLocalStorage } from '@/core/hooks/useLocalStorage';

type Props = {
  children: React.ReactNode;
};

type FlashMessageContextType = {
  flashMessage: TFlashMessage | null;
  showFlashMessage: (
    message: string,

    type?: 'success' | 'error' | 'warning' | 'info' | null,
    subMessage?: string,
  ) => void;
  clearFlashMessage: () => void;
};

const FlashMessageContext = createContext<FlashMessageContextType | undefined>(
  undefined,
);

const FlashMessageProvider = ({ children }: Props): JSX.Element => {
  const [flashMessage, setFlashMessage] = useState<TFlashMessage | null>(null);
  const [isFullDrawerOpen] = useLocalStorage('isFullDrawerOpen', false);

  const showFlashMessage = (
    message: string,
    type?: 'success' | 'error' | 'warning' | 'info' | null,
    subMessage?: string,
  ): void => {
    setFlashMessage({ message, type: type || null, subMessage });
  };

  const clearFlashMessage = (): void => {
    setFlashMessage(null);
  };

  return (
    <FlashMessageContext.Provider
      value={{ flashMessage, showFlashMessage, clearFlashMessage }}
    >
      {children}
      {flashMessage && (
        <FlashMessage
          flash={flashMessage}
          position={isFullDrawerOpen ? '288px' : '110px'}
        />
      )}
    </FlashMessageContext.Provider>
  );
};

const useFlashMessage = (): FlashMessageContextType => {
  const context = useContext(FlashMessageContext);
  if (!context) {
    throw new Error(
      'useFlashMessage must be used within a FlashMessageProvider',
    );
  }
  return context;
};

export { FlashMessageProvider, useFlashMessage };
